/**
 * fix-073 — hang-point and price-tag seeding, as the master-data product
 * screen does it (`components/masterdata/products/AddNew/addnew.js`).
 *
 * The Products screen does not print these four values as the catalog holds
 * them. It SEEDS its form and writes the seed only if the planner saves:
 *
 * - `addnew.js:128-137` (componentDidMount) — a product that has BOTH
 *   dimensions and NEITHER hole value gets `haggingHorizontal = width / 2`,
 *   `haggingVertical = 0.5`.
 * - `addnew.js:717-723` (handleChangeDetails) — a Width edit re-centres the
 *   hole, a Height edit puts its drop back to 0.5.
 * - `addnew.js:1643/1647` — the price-tag inputs render `priceTagGap || 0.5`
 *   and `priceTagHeight || 0.5`.
 *
 * Those seeds reach the catalog only on save, so most rows still hold 0 and
 * the seeding IS what the Products screen shows. The simulation's Product
 * Information modal edits the SAME master-data record through the same
 * `findProdByID` endpoint, but printed the raw row — so a product the
 * Products screen showed as "6.5 / 0.5" came up as "0 / 0" on the canvas
 * (reported 2026-10-26), and saving from there wrote a hole at the
 * product's top-left corner.
 *
 * The all-or-nothing guard is `addnew.js`'s and is kept EXACTLY, because a
 * half-empty pair is a real value here, not an unset one:
 *
 * - `display-option.service.ts` `defineAndHang` writes `(0, 0)` on purpose
 *   ("a documented placeholder", story 4.7 AC2);
 * - `useDragController.ensureHangPoint` resolves a missing vertical to 0
 *   deliberately — "the hook passes through the top edge";
 * - fix-045's own report reads "Product Information said (4, 0)".
 *
 * So a stored `{7, 0}` is a hole a planner or the app chose, and seeding it
 * per field would both disagree with the Products screen and move hung
 * placements by 0.5 cm on the next unrelated save.
 *
 * The same rule serves the DISPLAY OPTIONS surfaces through
 * `seedOptionHangHoleCm` below, measured against the option's own
 * footprint. It is applied where a hole is SHOWN or deliberately WRITTEN
 * (the option form, "Define and hang"), never at resolution time:
 * `effectiveHangingPoint` still falls back to the product's hole for an
 * option that has none, which is the 2026-09-21 user decision and is what
 * keeps already-hung products where they are.
 */

import type { CanonicalDisplayOption } from './contract.types'
import {
  DEFAULT_HANG_VERTICAL_CM,
  effectiveDimsForOption,
  optionOwnHangHoleCm,
  type BaseDims,
} from './display-options'

/** The legacy drop below the product's top edge (`defaultObjectLoad`).
 *  fix-084 moved the literal next to the resolver that now shares it
 *  (`effectiveHangingPoint` seeds the same hole this module displays), so
 *  the two can never drift; this alias keeps every existing caller. */
export const DEFAULT_HANG_VERTICAL = DEFAULT_HANG_VERTICAL_CM
/** The legacy price-tag gap/height fallback (`|| 0.5` on both inputs). */
export const DEFAULT_PRICE_TAG = 0.5

/** A finite number, or null for blank / absent / unparseable. */
const finiteOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/** Legacy truthiness: 0, null, undefined, '' and NaN are all "not set". */
const setOrNull = (value: unknown): number | null => {
  const num = finiteOrNull(value)
  return num === null || num === 0 ? null : num
}

/** A stored hole, in whatever unit the caller is working in. */
export interface HangPoint {
  h: unknown
  v: unknown
}

/**
 * The hole to show for a stored pair — `addnew.js:128-137` exactly.
 *
 * Both dimensions present and NEITHER axis set → the seeded centre hole.
 * Anything else is returned as the row holds it (an unparseable or absent
 * axis reads 0, which is what the legacy form's number input shows).
 */
export const seedHangPoint = (
  stored: HangPoint,
  dims: { width: unknown; height: unknown }
): { h: number; v: number } => {
  const h = setOrNull(stored.h)
  const v = setOrNull(stored.v)
  const width = setOrNull(dims.width)
  const height = setOrNull(dims.height)
  if (width !== null && height !== null && h === null && v === null) {
    return { h: width / 2, v: DEFAULT_HANG_VERTICAL }
  }
  return { h: h ?? 0, v: v ?? 0 }
}

/**
 * The hole's horizontal after a Width edit — `addnew.js:719`, which assigns
 * `width / 2` unconditionally, over a hole the planner set by hand.
 */
export const hangHorizontalForWidth = (width: unknown): number => {
  const num = finiteOrNull(width)
  return num === null ? 0 : num / 2
}

/** Price-tag gap / height: the stored value, else the legacy 0.5. */
export const priceTagOrDefault = (stored: unknown): number =>
  setOrNull(stored) ?? DEFAULT_PRICE_TAG

/** The master-data fields this module seeds (legacy spelling). */
export interface MasterHangableRow {
  haggingHorizontal?: unknown
  haggingVertical?: unknown
  priceTagGap?: unknown
  priceTagHeight?: unknown
}

/** The four form inputs, as strings — the modal edits them as text. */
export interface SeededHangable {
  hangH: string
  hangV: string
  priceTagGap: string
  priceTagHeight: string
}

/**
 * Seed the Hangable card from a freshly fetched master-data row.
 *
 * `dims` are the Width / Height the FORM is showing (the product's own uom,
 * which is also what master data stores), so a seeded hole always agrees
 * with the two numbers above it.
 */
/**
 * The hole a DISPLAY OPTION should show or be given, from that option's OWN
 * footprint — the orientation's permuted width/height, or a display mode's
 * stored dims — instead of the product's base ones. A laydown is a
 * different box from Front, so its centre is a different number.
 *
 * `baseDimsCm` is the product's base footprint in CANONICAL CM (`dims.wCm`
 * and friends), because the option-level hole fields are read as cm by
 * every consumer: they have no uom control of their own and `peg-snap`
 * takes them straight through (`effectiveHangingPoint`'s header). A display
 * mode's own dims carry their own uom, so those are converted; an
 * orientation's come from `baseDimsCm` and are already canonical. The 0.5
 * drop is likewise 0.5 cm here, where the Product Information modal's 0.5
 * is in the product's uom and converted on save.
 *
 * Returns null when the option's dims cannot be resolved (a display mode
 * with no dims, a corrupt orientation key) — the caller then leaves the
 * hole alone rather than inventing one from nothing.
 */
export const seedOptionHangHoleCm = (
  option: CanonicalDisplayOption | undefined,
  baseDimsCm: BaseDims
): { x: number; y: number } | null => {
  const dims = effectiveDimsForOption(option, baseDimsCm)
  if (!dims) return null
  // fix-084: `optionOwnHangHoleCm` IS `seedHangPoint`'s rule measured on the
  // option's own footprint — the canvas resolver now reads the hole through
  // the same function, so what this card shows and what the hook hangs from
  // cannot disagree. `{0, 0}` where that returns null keeps this function's
  // own contract: a zero-sized box still renders the legacy form's 0s.
  const seeded = optionOwnHangHoleCm(option, baseDimsCm)
  return seeded ? { x: seeded.h, y: seeded.v } : { x: 0, y: 0 }
}

export const seedHangableFromMaster = (
  row: MasterHangableRow,
  dims: { width: unknown; height: unknown }
): SeededHangable => {
  const point = seedHangPoint(
    { h: row.haggingHorizontal, v: row.haggingVertical },
    dims
  )
  return {
    hangH: String(point.h),
    hangV: String(point.v),
    priceTagGap: String(priceTagOrDefault(row.priceTagGap)),
    priceTagHeight: String(priceTagOrDefault(row.priceTagHeight)),
  }
}
